import { useReducer, useState, useEffect, useMemo } from 'react'
import Big from 'big.js'

import prettyAmount from 'src/utils/prettyAmount'
import calcFees from 'src/utils/calcFees'
import { nextTipOption } from 'src/utils/tipOptions'

const calcTip = (bigAmount, currency, { otherTip, tipOption, tipText }) => {
  const bigTipOption = Big(tipOption || 0)
  return otherTip
  ? Big(tipText || 0).toFixed(currency.exponent)
  : bigTipOption.gte(1)
  ? bigTipOption.toFixed(currency.exponent)
  : bigAmount.times(bigTipOption).round(currency.exponent, 0).toFixed(currency.exponent)
}

const findTippingConfig = ({ tipping, amount }) => {
  if (tipping.type != 'tipping') return null

  const bigAmount = Big(amount || 0)
  return tipping.auto
  && tipping.configs.find(config => bigAmount.gte(config.threshold))
  || tipping.fixed
}

const reducer = (state, action) => {
  switch (action.type) {
  case 'setCurrency': {
    const { value, presets } = action
    // Carry the preset selection across currency changes by index —
    // when switching from USD to EUR with the $50 preset selected the
    // donor lands on the EUR preset at the same position. Falls
    // through to the last preset if the new currency has fewer.
    const carry = state.presetIndex > -1
      ? presets[state.presetIndex]?.amount || presets.at(-1)?.amount
      : state.amount
    return {
      ...state,
      currency: value,
      amount: carry,
    }
  }
  case 'setAmount': {
    const { amount, presetIndex } = action
    return { ...state, amount, presetIndex }
  }
  case 'toggleOtherTip': {
    const otherTip = !state.otherTip
    const tipText = otherTip
    ? calcTip(Big(state.amount || 0), action.currency, { tipOption: state.tipOption })
    : state.tipText
    return { ...state, otherTip, tipText }
  }
  case 'resetTipping':
    return { ...state, tipOption: nextTipOption({
      declined: state.tipDeclined,
      config: action.tippingConfig,
      zeroTip: action.zeroTip,
    }) }
  case 'setTipOption':
    return {
      ...state,
      tipOption: action.value,
      otherTip: false, // reset otherTip for select
      // Only a pick from the menu counts as declining — see nextTipOption.
      tipDeclined: Big(action.value || 0).eq(0),
    }
  case 'setTipText':
    return { ...state, tipText: action.value }
  case 'setCoverCosts':
    return { ...state, coverCosts: action.value }
  case 'setFieldValue':
    return { ...state, [action.field]: action.value }
  case 'setAddress':
    return { ...state, ...action.value }
  case 'setSubscription':
    return { ...state, subscription: action.value }
  default:
    throw new Error('Invalid reducer action')
  }
}

const useDonation = ({
  amount, currencies, presets: presetsByCurrency, min_amount, blank_amount,
  order, tipping, collect_address, feeConfig, canadianReceipt, giftAid,
  subscription,
}) => {
  const [ state, dispatch ] = useReducer(reducer, amount, originalAmount => {
    const amount = blank_amount ? '' : prettyAmount(originalAmount)
    return {
      currency: order.currency,
      amount,
      presetIndex: (presetsByCurrency[order.currency] || [])
        .findIndex(p => p.amount === amount),
      coverCosts: tipping.type == 'cover_costs',
      tipOption: findTippingConfig({ tipping, amount })?.default,
      tipText: '',
      otherTip: false,
      tipDeclined: false,

      firstName:    order.user_first_name   || '',
      lastName:     order.user_last_name    || '',
      email:        order.email             || '',
      phone:        order.user_phone_number || '',
      subscription,
    }
  })
  const [ shares, setShares ] = useState(order.shares)

  const currency = useMemo(() =>
    currencies.find(currency => currency.id == state.currency)
  , [ state.currency ])
  const minAmount = useMemo(() =>
    Big(min_amount).times(currency.rate).round(currency.exponent, 3)
  , [ min_amount, currency ])
  const tippingConfig = useMemo(() =>
    findTippingConfig({ tipping, amount: Math.max(state.amount, minAmount) })
  , [ tipping, state.amount ])

  useEffect(() => {
    if (tippingConfig)
      dispatch({ type: 'resetTipping', tippingConfig, zeroTip: tipping.zeroTip })
  }, [ tippingConfig ])

  // The active currency's preset list — array of `{ amount, impact?, emoji? }`.
  // Amount.jsx iterates this to render preset buttons, and renders the matched
  // preset's impact + emoji as a callout when the fundraiser qualifies.
  const presets = presetsByCurrency[state.currency] || []
  const bigAmount = Big(state.amount || 0)

  // The donor's gift, normalized to the currency's scale — what they chose to
  // give, never including a covered fee or a tip. Distinct from `total` (what
  // gets charged). `state.amount` is raw donor input and isn't reliably scaled,
  // so don't substitute it. Reported to the embedding page as `amount`; see
  // utils/messages/completedDonation.js.
  const giftAmount = bigAmount.toFixed(currency.exponent)

  const fees = tipping.type == 'cover_costs' ? calcFees(bigAmount, currency, feeConfig) : null
  const tip = tipping.type == 'tipping' ? calcTip(bigAmount, currency, state) : null
  const total = (() => {
    switch (tipping.type) {
    case 'cover_costs':
      return (state.coverCosts ? bigAmount.plus(fees) : bigAmount).toFixed(currency.exponent)
    case 'tipping':
      return bigAmount.plus(tip).toFixed(currency.exponent)
    default:
      return bigAmount.toFixed(currency.exponent)
    }
  })()

  const usdAmount = bigAmount.div(currency.rate)
  const collectAddress = collect_address
  || usdAmount.gte(5000)
  || canadianReceipt?.value
  || giftAid?.value

  const setCurrency = value =>
    dispatch({ type: 'setCurrency', presets: presetsByCurrency[value] || [], value })

  const setAmount = (amount, presetIndex = -1) =>
    dispatch({ type: 'setAmount', amount, presetIndex })

  const toggleOtherTip = () => dispatch({ type: 'toggleOtherTip', currency })

  const setSubscription = value => dispatch({ type: 'setSubscription', value })

  return {
    state, dispatch, currency, presets, fees, tip, total, giftAmount,
    bigAmount, minAmount,
    collectAddress, setCurrency, setAmount, toggleOtherTip, setSubscription,
    shares, setShares, tippingConfig, usdAmount,
    // See the same field in donate_v7/useDonation.js: kept off
    // tippingConfig so respreading it can't retrigger resetTipping.
    zeroTip: tipping.zeroTip,
  }
}

export default useDonation
